'use client';

import { useCallback, useMemo, useRef, useState } from 'react';
import { AuthSessionApiError, reportAuthSessionFailure } from '@/app/utils/auth-session-api';
import { InteractionApiError, reportInteractionFailure } from '@/app/utils/auth-interactions-api';
import {
  MODERN_AUTH_EVENTS,
  type ModernAuthFlow,
  modernAuthEventProperties,
  rememberModernAuthApproval,
} from '@/app/utils/auth-analytics';
import {
  clearMagicLinkRequestContext,
  markMagicLinkRequestContext,
} from '@/components/auth/magic-link-context';
import { useTracking } from '@/components/tracking-provider';
import InteractionLoginForm from './login-form';
import InteractionSignupForm from './signup-form';
import MagicLinkConsumeState from './magic-link-consume-state';
import MagicLinkPendingState from './magic-link-pending-state';
import MagicLinkRequestForm from './magic-link-request-form';
import TerminalInteractionStateView, { terminalStateFromError } from './terminal-state';
import {
  INTERACTION_TERMINAL_REASONS,
  type AuthInteractionMode,
  type InteractionCompletion,
  type InternalInteractionMode,
  type TerminalInteractionState,
} from './types';

function initialScreenMode({
  initialMode,
  magicLinkToken,
}: {
  initialMode: AuthInteractionMode;
  magicLinkToken?: string;
}): InternalInteractionMode {
  if (magicLinkToken) {
    return 'magic_link_consume';
  }
  return initialMode;
}

function analyticsFlowForMode(mode: InternalInteractionMode): ModernAuthFlow {
  if (mode === 'magic_link' || mode === 'magic_link_pending' || mode === 'magic_link_consume') {
    return 'magic_link';
  }
  return mode;
}

export function redirectInteractionCompletion(): InteractionCompletion {
  return {
    mode: 'redirect',
    complete: (resumeUrl) => {
      window.location.assign(resumeUrl);
    },
  };
}

function safeClientReturnTo(destination: string): string {
  if (
    !destination.startsWith('/') ||
    destination.startsWith('//') ||
    destination.startsWith('/\\')
  ) {
    return '/';
  }
  return destination;
}

export function startRedirectAuthTransaction(returnTo?: string): void {
  const destination =
    returnTo || `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const startUrl = new URL('/api/auth/start', window.location.origin);
  startUrl.searchParams.set('return_to', safeClientReturnTo(destination));
  window.location.assign(startUrl.toString());
}

export default function AuthInteractionScreen({
  interactionToken,
  completion,
  onRestart,
  initialMode = 'login',
  magicLinkToken,
  sameDeviceMagicLink = false,
  hideHeader = false,
  minimalChrome = false,
}: {
  interactionToken: string;
  completion: InteractionCompletion;
  onRestart: () => void;
  initialMode?: AuthInteractionMode;
  magicLinkToken?: string;
  sameDeviceMagicLink?: boolean;
  hideHeader?: boolean;
  minimalChrome?: boolean;
}) {
  const tracker = useTracking();
  const startingMode = useMemo(
    () => initialScreenMode({ initialMode, magicLinkToken }),
    [initialMode, magicLinkToken],
  );
  const [mode, setMode] = useState<InternalInteractionMode>(startingMode);
  const [magicLinkEmail, setMagicLinkEmail] = useState<string | undefined>();
  const [terminalState, setTerminalState] = useState<TerminalInteractionState | null>(null);
  const [completionError, setCompletionError] = useState<string | null>(null);
  const [pendingResumeUrl, setPendingResumeUrl] = useState<string | null>(null);
  const [isCompleting, setIsCompleting] = useState(false);
  const loginBeginTrackedRef = useRef(false);

  const trackLoginBegin = useCallback(
    (flow: ModernAuthFlow) => {
      if (loginBeginTrackedRef.current) return;
      loginBeginTrackedRef.current = true;
      tracker.trackEvent(
        MODERN_AUTH_EVENTS.loginBegin,
        modernAuthEventProperties({ flow, completionMode: completion.mode }, 'index.next'),
      );
    },
    [completion.mode, tracker],
  );

  const handleTerminal = useCallback(
    (error: InteractionApiError | TerminalInteractionState) => {
      if (
        (error instanceof InteractionApiError && error.kind === 'expired') ||
        (!(error instanceof InteractionApiError) &&
          error.reason === INTERACTION_TERMINAL_REASONS.expired)
      ) {
        tracker.trackEvent(
          MODERN_AUTH_EVENTS.interactionExpired,
          modernAuthEventProperties({
            flow: analyticsFlowForMode(mode),
            completionMode: completion.mode,
          }),
        );
      }
      if (error instanceof InteractionApiError) {
        setTerminalState(terminalStateFromError(error));
        return;
      }
      setTerminalState(error);
    },
    [completion.mode, mode, tracker],
  );

  const handleApproved = useCallback(
    async (resumeUrl: string): Promise<boolean> => {
      setCompletionError(null);
      setPendingResumeUrl(resumeUrl);
      setIsCompleting(true);
      clearMagicLinkRequestContext(interactionToken);
      const flow = analyticsFlowForMode(mode);
      if (flow === 'magic_link') {
        tracker.trackEvent(
          MODERN_AUTH_EVENTS.magicLinkApproved,
          modernAuthEventProperties({ flow, completionMode: completion.mode }),
        );
      }
      rememberModernAuthApproval({ flow, completionMode: completion.mode });
      try {
        await completion.complete(resumeUrl);
        setPendingResumeUrl(null);
        return true;
      } catch (error) {
        if (error instanceof AuthSessionApiError) {
          reportAuthSessionFailure(error, {
            source: 'auth-interaction-screen',
            operation: 'complete',
          });
        } else {
          reportInteractionFailure(error, {
            source: 'auth-interaction-screen',
            operation: 'complete',
            completionMode: completion.mode,
          });
        }
        if (completion.mode === 'embedded') {
          // Emerge resume tokens are single-use and the BFF clears the
          // transaction cookie for a failed completion. Retrying the same
          // resume URL can never recover; mint a fresh transaction instead.
          setTerminalState({
            title: 'Unable to complete sign in',
            message:
              error instanceof AuthSessionApiError
                ? error.message
                : 'Your approval could not be completed. Start over to try again.',
            actionLabel: 'Start over',
          });
          setPendingResumeUrl(null);
          return false;
        }
        setCompletionError('Authentication was approved, but completion failed. Please try again.');
        return false;
      } finally {
        setIsCompleting(false);
      }
    },
    [completion, interactionToken, mode, tracker],
  );

  const switchMode = useCallback((nextMode: InternalInteractionMode) => {
    setCompletionError(null);
    setPendingResumeUrl(null);
    setIsCompleting(false);
    setTerminalState(null);
    setMode(nextMode);
  }, []);

  const handleRestart = useCallback(() => {
    tracker.trackEvent(
      MODERN_AUTH_EVENTS.interactionRestarted,
      modernAuthEventProperties({
        flow: analyticsFlowForMode(mode),
        completionMode: completion.mode,
      }),
    );
    onRestart();
  }, [completion.mode, mode, onRestart, tracker]);

  const handleLoginBegin = useCallback(() => {
    trackLoginBegin('login');
  }, [trackLoginBegin]);

  if (!interactionToken) {
    return (
      <TerminalInteractionStateView
        state={{
          title: 'This sign-in request is missing',
          message: 'Start over to continue signing in.',
          actionLabel: 'Start over',
        }}
        onRestart={onRestart}
      />
    );
  }

  if (terminalState) {
    return <TerminalInteractionStateView state={terminalState} onRestart={handleRestart} />;
  }

  return (
    <div className="w-full max-w-lg">
      {completionError && (
        <div className="form-error rounded-sm border border-red-200 bg-red-50 p-3">
          <div>{completionError}</div>
          {pendingResumeUrl ? (
            <button
              type="button"
              onClick={() => void handleApproved(pendingResumeUrl)}
              className="mt-2 underline font-medium"
              disabled={isCompleting}
            >
              {isCompleting ? 'Trying again...' : 'Try again'}
            </button>
          ) : null}
        </div>
      )}

      {mode === 'login' && (
        <InteractionLoginForm
          interactionToken={interactionToken}
          onApproved={handleApproved}
          onTerminal={handleTerminal}
          onBegin={handleLoginBegin}
          onSwitchToSignup={() => switchMode('signup')}
          onSwitchToMagicLink={() => switchMode('magic_link')}
          hideHeader={hideHeader}
          allowOutboundLinks={!minimalChrome}
        />
      )}

      {mode === 'signup' && (
        <InteractionSignupForm
          interactionToken={interactionToken}
          onApproved={handleApproved}
          onTerminal={handleTerminal}
          onSwitchToLogin={() => switchMode('login')}
          hideHeader={hideHeader}
          allowOutboundLinks={!minimalChrome}
        />
      )}

      {mode === 'magic_link' && (
        <MagicLinkRequestForm
          interactionToken={interactionToken}
          onRequested={(email) => {
            trackLoginBegin('magic_link');
            tracker.trackEvent(
              MODERN_AUTH_EVENTS.magicLinkRequested,
              modernAuthEventProperties({ flow: 'magic_link', completionMode: completion.mode }),
            );
            markMagicLinkRequestContext(interactionToken);
            setMagicLinkEmail(email);
            switchMode('magic_link_pending');
          }}
          onTerminal={handleTerminal}
          onSwitchToLogin={() => switchMode('login')}
          onSwitchToSignup={() => switchMode('signup')}
          hideHeader={hideHeader}
        />
      )}

      {mode === 'magic_link_pending' && (
        <MagicLinkPendingState
          interactionToken={interactionToken}
          email={magicLinkEmail}
          onApproved={handleApproved}
          onTerminal={handleTerminal}
          onSwitchToLogin={() => switchMode('login')}
        />
      )}

      {mode === 'magic_link_consume' && magicLinkToken && (
        <MagicLinkConsumeState
          interactionToken={interactionToken}
          magicLinkToken={magicLinkToken}
          sameDevice={sameDeviceMagicLink}
          onApproved={handleApproved}
          onTerminal={handleTerminal}
          onRestart={handleRestart}
        />
      )}
    </div>
  );
}
